import { SkillType } from './common.interfaces';
import type { ProductCode } from './products.interface';

export type Scale = 'CEFR' | 'VERSANT' | 'GSE' | 'LEVEL';

export enum LicenseStatus {
  NEW = 'NEW',
  VALIDATED = 'VALIDATED',
  SCORED = 'SCORED',
  CERTIFICATION_FAILED = 'CERTIFICATION_FAILED',
  CERTIFICATION_REVOKED = 'CERTIFICATION_REVOKED',
  APPEALED = 'APPEALED',
  CERTIFIED = 'CERTIFIED',
  FINALIZED = 'FINALIZED',
  SPEAKING_UNSCORBALE = 'SPEAKING_UNSCORBALE',
  LATE_HANGUP = 'LATE_HANGUP',
  USER_HUNG_UP = 'USER_HUNG_UP',
  IN_PROGRESS = 'IN_PROGRESS',
  STARTED = 'STARTED',
  ERROR = 'ERROR',
  NOT_SCORABLE = 'NOT_SCORABLE',
  PARTIALLY_SCORED = 'PARTIALLY_SCORED',
  COMPLETED = 'COMPLETED',
  UPLOADING = 'UPLOADING',
  ABORTED = 'ABORTED',
  EXPIRED = 'EXPIRED'
}

export interface Score {
  scale: Scale;
  component: SkillType;
  score: string;
  color?: string;
  exceptionCode?: number;
  exceptionMessage?: string;
  tags?: any;
  description?: string;
  label?: string;
}

export interface Product {
  active: boolean;
  application: {
    id: number;
    code: string;
    name: string;
  };
  code: ProductCode;
  id: number;
  name: string;
  price: number;
  repositoryId: number;
  practice?: boolean;
  essId: number;
  metadata: {
    TEST_DURATION: string;
  };
}

export interface License {
  createdAt: string;
  startTime: string | null;
  endTime: string | null;
  errorCode: string | null;
  errorReason: string | null;
  expiresAt: string;
  id: number;
  proctored: boolean;
  product: Product;
  scores: Score[];
  status: LicenseStatus;
  testCode: string;
  testTakerId: string;
  canBeRetaken: boolean;
  retakeRedeemed: boolean;
  retake: boolean;
  retakeParentLicense?: License | null;
  issued: Date;
  retakeExpiresAt: Date;
  requiresUserAction: boolean;
}

export interface SkillScore {
  description: string;
  label: string;
  name: string;
  score: string;
  value: string;
}

export interface Skill {
  component: string;
  definition: string;
  description: string;
  scores: SkillScore[];
  tags: string[];
  tips: string[];
  exceptionCode: number;
  exceptionMessage: string;
}

export interface LicenseResolution {
  id: number;
  ticketType: string;
  resolutionType: string;
  value: string;
}

export interface FailedCertificationData {
  id: number;
  canBeRetaken?: boolean;
  retake: boolean;
}

export const statusesWithDetailsPage: LicenseStatus[] = [LicenseStatus.CERTIFIED];

export const statusesWithoutNewAndValidated = [
  LicenseStatus.STARTED,
  LicenseStatus.COMPLETED,
  LicenseStatus.LATE_HANGUP,
  LicenseStatus.USER_HUNG_UP,
  LicenseStatus.ERROR,
  LicenseStatus.NOT_SCORABLE,
  LicenseStatus.PARTIALLY_SCORED,
  LicenseStatus.SCORED,
  LicenseStatus.CERTIFICATION_FAILED,
  LicenseStatus.CERTIFICATION_REVOKED,
  LicenseStatus.CERTIFIED,
  LicenseStatus.FINALIZED,
  LicenseStatus.ABORTED
] as LicenseStatus[];

export interface TestsLimitReached {
  testLimitReached: boolean;
  message: string;
}

export type IdVerificationStatus = 'UNKNOWN' | 'STARTED' | 'PENDING' | 'FAIL' | 'SUCCESS';

export interface PreferredLicense {
  id: number;
  testCode: string;
  referenceId: string;
  createdAt: string;
  expiresAt: string;
  startTime: string;
  endTime: string;
  status: LicenseStatus;
  errorCode: any;
  errorReason: any;
  testTakerId: string;
  proctored: boolean;
  retake: boolean;
  certificate: unknown;
  product: Product;
  idVerificationStatus: IdVerificationStatus;
  scores: Score[];
}

export interface PreferredLicensesResponse {
  available: PreferredLicense[];
  inProgressOrCertifiedOrFailed: PreferredLicense[];
  lastOrNext: License[];
}
